import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { firstValueFrom, interval, scan, Subject, takeUntil, takeWhile, tap } from 'rxjs';
import { Message } from './messages.types';
import { MessagesService } from './messages.service';

@Component({
    selector: 'messages',
    templateUrl: './messages.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'messages',
    standalone: false
})
export class MessagesComponent implements OnInit, OnDestroy {
    @ViewChild('container') messageContainer;
    @ViewChild('messagesOrigin') private _messagesOrigin: MatButton;
    @ViewChild('messagesPanel') private _messagesPanel: TemplateRef<any>;

    newMessage: Message = {};

    showFilters = false;
    filtersGroup = new FormGroup({
        ackPending: new FormControl(false),
        rcPending: new FormControl(false)
    });

    component: 'list' | 'create' = 'list';

    private _overlayRef: OverlayRef;
    private _unsubscribeAll: Subject<any> = new Subject<any>();



    /**
     * Constructor
     */
    constructor(
        private _messagesService: MessagesService,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef,
    ) { }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this._messagesService.openPanel$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((message: any) => {
                this.openPanel(message);
            });

        this._messagesService.closePanel$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                this.closePanel();
            });

        this._messagesService.goToList$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(async () => {
                await this.goToListView();
            });

        this.filtersGroup.valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe(async (filters) => {
            await firstValueFrom(this._messagesService.getFirst(filters));
        });

    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();

        // Dispose the overlay
        if (this._overlayRef) {
            this._overlayRef.dispose();
        }
    }



    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Open the messages panel
     */
    openPanel(message?: any): void {
        // Return if the messages panel or its origin is not defined
        if (!this._messagesPanel || !this._messagesOrigin) {
            return;
        }

        if (!message) {
            this.newMessage = {};
            this.component = 'list';
        } else {
            this.newMessage = message;
            this.component = 'create';
        }

        // Create the overlay if it doesn't exist
        if (!this._overlayRef) {
            this._createOverlay();
        }

        // Attach the portal to the overlay
        this._overlayRef.attach(new TemplatePortal(this._messagesPanel, this._viewContainerRef));
    }

    goToCreateView() {
        this.component = 'create';
        this.showFilters = false;
        this.newMessage = {};
    }

    async goToListView() {
        this.component = 'list';
        await this.refresh();
    }

    async refresh() {
        await this.scrollToTop();
        if (this.showFilters) {
            await firstValueFrom(this._messagesService.getFirst({ ackPending: this.getControl('ackPending').value, rcPending: this.getControl('rcPending').value }));
        } else {
            await firstValueFrom(this._messagesService.getFirst());
        }
    }

    /**
     * Close the messages panel
     */
    closePanel(): void {
        this._overlayRef.detach();
    }

    async onScroll(event) {
        // visible height + pixel scrolled >= total height
        if (this._messagesService.lastTimestamp) {
            if (event.target.offsetHeight + event.target.scrollTop >= event.target.scrollHeight) {
                if (this.showFilters) {
                    await firstValueFrom(this._messagesService.get({ ackPending: this.getControl('ackPending').value, rcPending: this.getControl('rcPending').value }));
                } else {
                    await firstValueFrom(this._messagesService.get());
                }
            }
        }
    }

    async scrollToTop() {
        const duration = 300;
        const period = 5;
        const move = this.messageContainer?.nativeElement.scrollTop * period / duration;
        await new Promise<void>((resolve) => {
            interval(period).pipe(
                scan((acc, curr) => acc - move, this.messageContainer.nativeElement.scrollTop),
                tap(position => this.messageContainer.nativeElement.scrollTop = position),
                takeWhile(val => val > 0)).subscribe({
                    complete: () => resolve()
                });
        });
    }

    async toggleFilters() {
        this.showFilters = !this.showFilters;
        await this.scrollToTop();
        if (this.showFilters) {
            await firstValueFrom(this._messagesService.getFirst({ ackPending: this.getControl('ackPending').value, rcPending: this.getControl('rcPending').value }));
        } else {
            await firstValueFrom(this._messagesService.getFirst());
        }
    }

    getControl(name) {
        return this.filtersGroup.get(name);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Create the overlay
     */
    private _createOverlay(): void {
        // Create the overlay
        this._overlayRef = this._overlay.create({
            hasBackdrop: true,
            backdropClass: 'fuse-backdrop-on-mobile',
            scrollStrategy: this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay.position()
                .flexibleConnectedTo(this._messagesOrigin._elementRef.nativeElement)
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'top'
                    },
                    {
                        originX: 'start',
                        originY: 'top',
                        overlayX: 'start',
                        overlayY: 'bottom'
                    },
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'top'
                    },
                    {
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'end',
                        overlayY: 'bottom'
                    }
                ])
        });

        // Detach the overlay from the portal on backdrop click
        this._overlayRef.backdropClick().subscribe(() => {
            this._overlayRef.detach();
        });
    }
}
